import React, { useRef, useEffect } from "react"
import Sidebar from "../../components/layout/sidebar"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import { Link } from "gatsby"
import { graphql, useStaticQuery } from "gatsby"
import { getImage } from "gatsby-plugin-image"
import { navigate } from "@reach/router"
import useDpt from "../../hooks/use-dpt"
import useConcepts from "../../hooks/use-all-concepts"

import { BgImage } from "gbimage-bridge"

const Plan = styled.div`
  position: relative;
  flex-basis: 33%;
  min-height: 300px;
  margin: 0 10px 10px 10px;
  background: lightgray;
  border: thin #c0c0c0 solid;
`

const imageStyles = css`
  position: absolute !important;
  height: 100%;
  width: 100%;
  filter: invert(10%);
`
//  position: absolute;
const BlockStyle = css`
  display: block;
  top: 0;
  width: 100%;
  height: 100%;
  color: white;
  font-size: 1.2rem;
  display: flex;
  transition: opacity 0.3s;
  align-items: center;
  position: absolute;
  justify-content: center;
  text-align: center;
  opacity: 0;
  padding: 20px;
  font-weight: 500;
  @media (max-width: 768px) {
    opacity: 1;
    background: rgba(0, 0, 0, 0.5);
  }
  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.7);
  }
`

const GeneralPlanImage = ({ children, src }) => {
  const {
    allFile: { edges: allEdges },
  } = useStaticQuery(
    graphql`
      query {
        allFile(
          filter: {
            extension: { regex: "/(jpg)|(png)|(jpeg)/" }
            relativeDirectory: { eq: "key-projects" }
          }
        ) {
          edges {
            node {
              extension
              relativePath
              childImageSharp {
                gatsbyImageData(
                  width: 400
                  quality: 100
                  placeholder: TRACED_SVG
                  formats: [AUTO, WEBP, AVIF]
                )
              }
            }
          }
        }
      }
    `
  )

  const imageWithSrc = allEdges.find(image => image.node.relativePath === src)
  const image = getImage(imageWithSrc.node)

  return (
    <BgImage
      image={image}
      css={css`
        ${imageStyles}
      `}
    >
      {children}
    </BgImage>
  )
}

const ProjectsWrapper = styled.div`
  display: flex;
  ${props => props.theme.tablet} {
    flex-direction: column;
  }
  margin: 0 -10px 0 -10px;
`

const DptWrapper = styled.div`
  .wp-block-columns {
    display: flex;
    ${props => props.theme.tablet} {
      flex-direction: column;
    }
    margin: 0 -10px;
    .wp-block-column {
      flex-basis: 33%;
      position: relative;
      margin: 0 10px 20px 10px;
    }
  }
  .wp-block-image {
    background-color: lightgray;
    display: flex;
    flex-flow: column;
    padding: 1px;
    margin: auto;
    img {
      width: 100%;
      height: auto;
      filter: invert(25%);
      border: thin #c0c0c0 solid;
    }
  }

  .wp-block-file {
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    a {
      ${BlockStyle}
    }
  }

  h4 {
    font-size: 1.2rem;
    margin: 0rem 0 1.5rem;
  }
`

const Heading = styled.div`
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 20px;
  ${props => props.theme.tablet} {
    font-size: 1.3rem;
    font-weight: 500;
    text-align: center;
  }
`

const StyledLinkBlock = styled(Link)`
  ${BlockStyle}
`

const Concepts = styled(Link)`
  ${BlockStyle}
  height: 300px;
  font-size: 1.2rem;
`

const ConceptsWrapper = styled.div`
  flex-wrap: nowrap;
  display: flex;
  text-align: center;
  ${props => props.theme.tablet} {
    flex-direction: column;
  }
  margin: 0 -10px 0 -10px;
`
const ConceptWrapper = styled.div`
  position: relative;
  min-height: 300px;
  margin: 0 0 20px 0;
  background: lightgray;
`
const ConceptsColumn = styled.div`
  flex-basis: 33%;
  display: flex;
  margin: 0 10px;
  h4 {
    font-size: 1.2rem;
    margin: 0 0 1.5rem;
  }
  flex-direction: column;
`

const ConceptsBckgImg = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background-image: url("${props => props.src}");
  background-size: cover; /* <------ */
  background-repeat: no-repeat;
  background-position: center center;
  filter: invert(15%);
  border: thin #c0c0c0 solid;
`

const Concept = ({ concept }) => {
  return (
    <ConceptWrapper>
      <ConceptsBckgImg src={concept.conceptCF.cover.mediaItemUrl} />
      <Concepts to={`/portfolio/concept/${decodeURI(concept.slug)}`}>
        {concept.title}
      </Concepts>
    </ConceptWrapper>
  )
}

export default function Portfolio({ data }) {
  const headings = [
    "Ключевые проекты",
    "Концепции преобразования городских пространств",
    "Документация по планировке территории",
  ]
  const mapOfHeadings = useRef({})
  const dptData = useDpt()
  const allConcepts = useConcepts()

  const conceptsByType = allConcepts.reduceRight(
    (accumulator, currentValue) => {
      accumulator[currentValue.conceptCF.type].push(currentValue)
      return accumulator
    },
    {
      planning: [],
      architectural: [],
      beautification: [],
    }
  )

  useEffect(() => {
    const location = decodeURIComponent(window.location.hash.split("#")[1])
    if (location && mapOfHeadings.current.hasOwnProperty(location)) {
      handleScroll(location)
    }
  })

  const handleScroll = heading => {
    mapOfHeadings.current[heading].scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const handleShow = heading => {
    navigate(`#${encodeURIComponent(heading)}`, { replace: true })
  }

  const linksRender = list => {
    return list.map(element => {
      return (
        <li key={element}>
          <a onClick={() => handleShow(element)}>{element}</a>
        </li>
      )
    })
  }

  return (
    <Sidebar
      heading="Портфолио"
      sticky
      leftColumn={
        <div>
          <ul>{linksRender(headings)}</ul>
        </div>
      }
    >
      <Heading
        ref={heading => (mapOfHeadings.current["Ключевые проекты"] = heading)}
      >
        Ключевые проекты
      </Heading>
      <ProjectsWrapper>
        <Plan>
          <GeneralPlanImage src="key-projects/general.png" />
          <StyledLinkBlock to="/portfolio/general-plan">
            Генеральный план города Перми
          </StyledLinkBlock>
        </Plan>

        <Plan>
          <GeneralPlanImage src="key-projects/strategic.png" />
          <StyledLinkBlock to="/portfolio/strategic-plan">
            Стратегический мастер-план города Перми
          </StyledLinkBlock>
        </Plan>

        <Plan>
          <GeneralPlanImage src="key-projects/illumination.png" />
          <StyledLinkBlock to="/portfolio/illumination-plan">
            Мастер-план освещения города Перми
          </StyledLinkBlock>
        </Plan>
      </ProjectsWrapper>
      <Heading
        ref={heading =>
          (mapOfHeadings.current[
            "Концепции преобразования городских пространств"
          ] = heading)
        }
      >
        Концепции преобразования городских пространств
      </Heading>
      <ConceptsWrapper>
        <ConceptsColumn>
          <h4>Обьёмно-планировочные решения</h4>
          {conceptsByType.planning.map(concept => (
            <Concept concept={concept} key={concept.title} />
          ))}
        </ConceptsColumn>
        <ConceptsColumn>
          <h4>Архитектурные решения</h4>
          {conceptsByType.architectural.map(concept => (
            <Concept concept={concept} key={concept.title} />
          ))}
        </ConceptsColumn>
        <ConceptsColumn>
          <h4>Благоустройство</h4>
          {conceptsByType.beautification.map(concept => (
            <Concept concept={concept} key={concept.title} />
          ))}
        </ConceptsColumn>
      </ConceptsWrapper>

      <Heading
        ref={heading =>
          (mapOfHeadings.current[
            "Документация по планировке территории"
          ] = heading)
        }
      >
        Документация по планировке территории
      </Heading>
      <DptWrapper dangerouslySetInnerHTML={{ __html: dptData.content }} />
    </Sidebar>
  )
}
